import React from 'react';
import Layout from 'src/pages/Login/Layout';
import LoginForm from 'src/pages/Login/forms/LoginForm';
import { getHashParam } from 'src/hooks';
import { LoginResultType, useForgotPasswordMutation } from 'src/redux/services/auth';
import ForceChangePasswordForm from 'src/pages/Login/forms/ForceChangePasswordForm';
import PasswordExpired from 'src/pages/Login/components/PasswordExpired';
import LoadingScreen from 'src/frontend-common/components/LoadingScreen';

import VerificationCodePage from 'src/pages/Login/VerificationCode';
import { setAccessToken } from 'src/frontend-common/localstorage';
import { useNavigate } from 'react-router-dom';

type CurrentFormType = 'LOGIN' | 'FORCE_CHANGE_PASSWORD' | 'RECOVERY_PASSWORD_SENT' | 'NEW_PASSWORD_REQUIRED' | 'SMS_MFA';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentForm, setCurrentForm] = React.useState<CurrentFormType>('LOGIN');
  const [sendRecoverPassword] = useForgotPasswordMutation();
  const [currentPayload, setCurrentPayload] = React.useState<Record<string, string>>({});

  const accessToken = getHashParam('access_token') || '';

  const setTokenAndRedirect = (token: string) => {
    setAccessToken(token);
    const setUrl = (window.location.href = '/#access_token=' + token);
    setTimeout(setUrl, 500);
  };

  const handleLoginResult = async (result: LoginResultType, payload: Record<string, string>) => {
    if (result.status === 'ok') {
      return setTokenAndRedirect(result.token);
    }

    // The user have to recovery the password
    if (result.status === 'redirect_reset_password') {
      await sendRecoverPassword({ username: payload.username ?? '' });
      setCurrentForm('RECOVERY_PASSWORD_SENT');
    }

    // The user have to confirm email
    if (result.status === 'redirect_resend_confirmation') {
      navigate('/auth/thanks', { state: { username: payload.username } });
      return;
    }

    if (result.status === 'challenge' && result.challenge === 'NEW_PASSWORD_REQUIRED') {
      setCurrentPayload(payload);
      setCurrentForm('NEW_PASSWORD_REQUIRED');
    } else if (result.status === 'challenge') {
      setCurrentPayload(payload);
      setCurrentForm('FORCE_CHANGE_PASSWORD');
    }
    if (result.status === 'challenge' && result.challenge === 'SMS_MFA') {
      setCurrentPayload({
        username: payload.username ?? '',
        password: payload.password ?? '',
        token: result.token,
        session: result.session || '',
      });
      setCurrentForm('SMS_MFA');
    }
  };

  const handleChallengeComplete = (token: string) => {
    setTokenAndRedirect(token);
  };

  const handleOnBackToLogin = () => {
    setCurrentForm('LOGIN');
  };

  React.useEffect(() => {
    localStorage.removeItem('accessToken');

    if (accessToken && accessToken !== '') {
      setTokenAndRedirect(accessToken);
    }
  }, [accessToken]);

  if (accessToken) {
    return <LoadingScreen logo variant="none" />;
  }
  return (
    <>
      {currentForm === 'LOGIN' && (
        <Layout>
          <LoginForm onLoginResponse={handleLoginResult} />
        </Layout>
      )}
      {currentForm === 'SMS_MFA' && <VerificationCodePage payload={currentPayload} />}
      {currentForm === 'RECOVERY_PASSWORD_SENT' && <PasswordExpired onBackToLogin={handleOnBackToLogin} />}
      {currentForm === 'FORCE_CHANGE_PASSWORD' && (
        <ForceChangePasswordForm
          onChallengeComplete={handleChallengeComplete}
          initialValues={{
            username: currentPayload['username'] ?? '',
            password: currentPayload['password'] ?? '',
          }}
        />
      )}
      {currentForm === 'NEW_PASSWORD_REQUIRED' && (
        <ForceChangePasswordForm
          onChallengeComplete={handleChallengeComplete}
          text="Reset password"
          initialValues={{
            username: currentPayload['username'] ?? '',
            password: currentPayload['password'] ?? '',
          }}
        />
      )}
    </>
  );
};

export default LoginPage;
